import React from 'react';
import TelegramWebApp from '../../telegram-init';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import WebApp from '@twa-dev/sdk';

interface User {
  name: string;
  lastName: string;
  avatar?: string;
}

interface ProfileHeaderProps {
  user: User;
  onUserUpdate: (user: Partial<User>) => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ user, onUserUpdate }) => {
  const { t } = useTranslation();

  const languages = [
    { code: 'en', name: t('PROFILE_SCREEN.LANGUAGE.LANGUAGES.EN'), flag: '🇬🇧' },
    { code: 'ru', name: t('PROFILE_SCREEN.LANGUAGE.LANGUAGES.RU'), flag: '🇷🇺' },
    { code: 'uk', name: t('PROFILE_SCREEN.LANGUAGE.LANGUAGES.UK'), flag: '🇺🇦' },
    { code: 'es', name: t('PROFILE_SCREEN.LANGUAGE.LANGUAGES.ES'), flag: '🇪🇸' },
    { code: 'ar', name: t('PROFILE_SCREEN.LANGUAGE.LANGUAGES.AR'), flag: '🇸🇦' },
    { code: 'zh', name: t('PROFILE_SCREEN.LANGUAGE.LANGUAGES.ZH'), flag: '🇨🇳' }
  ];

  const handleLanguageChange = async (langCode: string) => {
    try {
      TelegramWebApp.HapticFeedback.impactOccurred('medium');
      
      i18n.changeLanguage(langCode);
      localStorage.setItem('i18nextLng', langCode);
      
      const telegramId = TelegramWebApp.initDataUnsafe?.user?.id;
      if (telegramId) {
        console.log('Sending language update request:', {
          telegramId,
          langCode,
          url: `${import.meta.env.VITE_API_URL}/api/v1/users/${telegramId}`
        });

        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/users/${telegramId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'x-telegram-init-data': WebApp.initData
          },
          body: JSON.stringify({ language_code: langCode })
        });

        const result = await response.json();
        console.log('Language update response:', result);
      }
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };

  return (
    <section className="flex flex-col items-center gap-3 relative">
      <div className="w-20 h-20 rounded-full overflow-hidden bg-tg-secondary-bg flex items-center justify-center">
        {user.avatar ? (
          <img 
            src={user.avatar} 
            alt="Profile" 
            className="w-full h-full object-cover"
            onError={() => {
              console.error('Failed to load avatar, switching to initials');
              onUserUpdate({ avatar: undefined });
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl font-semibold text-tg-text uppercase">
            {user.name.charAt(0)}{user.lastName ? user.lastName.charAt(0) : ''}
          </div>
        )}
      </div>
      <h1 className="text-xl font-semibold text-tg-text m-0">
        {user.lastName ? `${user.name} ${user.lastName}` : user.name}
      </h1>
      
      {/* Language Selector */}
      <div className="relative w-full max-w-[400px] mt-1 bg-tg-bg rounded-xl border border-pink-500/10 overflow-hidden">
        <select 
          value={i18n.language} 
          onChange={(e) => handleLanguageChange(e.target.value)}
          className="w-full py-3 pl-4 pr-9 bg-tg-bg border-none text-tg-text text-sm font-medium appearance-none cursor-pointer transition-colors duration-200 relative z-[2] text-left hover:bg-pink-500/5 focus:bg-pink-500/8 focus:outline-none"
        >
          {languages.map(lang => (
            <option key={lang.code} value={lang.code}>
              {`${lang.flag} ${lang.name}`}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-[70%] w-2 h-2 border-r-2 border-b-2 border-tg-text rotate-45 opacity-60 pointer-events-none z-[2]" />
      </div>
    </section>
  );
};

